import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download,
  RefreshCw,
  ShieldCheck,
  ChevronUp,
  ChevronDown,
  Search,
  Filter,
  Loader2,
  MoreHorizontal,
  Gauge,
  RefreshCcw,
  Megaphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { OverridesModal } from '../../components/Admin/OverridesModal';
import { useAuthStore } from '../../store/authStore';
import { tierLabel } from '../../constants/planLimits';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const AUTO_REFRESH_MS = 5 * 60 * 1000;

interface MetricTriple {
  used: number;
  base: number;
  addons?: number;
  override: number | null;
  effective: number;
}

interface StorageMetric {
  usedMb: number;
  base: number;
  addonMb: number;
  override: number | null;
  effective: number;
}

interface UsageRow {
  id: string;
  email: string;
  name: string;
  subscription: string;
  role: string;
  createdAt: string;
  pages: MetricTriple;
  customDomains: MetricTriple;
  storage: StorageMetric;
  submissionsThisMonth: { used: number; base: number; override: number | null; effective: number };
  aiGensThisMonth: { used: number; base: number; addonRemaining: number; override: number | null; effective: number };
  hasOverrides: boolean;
  overrideReason: string | null;
  overrideSetAt: string | null;
  lastActiveAt: string | null;
}

type SortKey =
  | 'email'
  | 'subscription'
  | 'pagesEffective'
  | 'storageUsedMb'
  | 'submissionsUsed'
  | 'aiUsed';

type PlanFilter = 'all' | 'free' | 'pro' | 'agency' | 'enterprise';

function formatCap(n: number): string {
  if (n === -1) return '∞';
  return n.toLocaleString();
}

/**
 * Returns true if the user is at or above 80% of the effective cap
 * for any metric (storage, submissions, AI, pages). Used by the
 * "near cap" filter. -1 effective = unlimited, never near.
 */
function isNearCap(u: UsageRow): boolean {
  const threshold = (used: number, eff: number) => eff > 0 && used / eff >= 0.8;
  if (u.storage.effective !== -1 && threshold(u.storage.usedMb, u.storage.effective)) return true;
  if (u.pages.effective !== -1 && threshold(u.pages.used, u.pages.effective)) return true;
  if (u.submissionsThisMonth.effective !== -1 && threshold(u.submissionsThisMonth.used, u.submissionsThisMonth.effective)) return true;
  if (u.aiGensThisMonth.effective !== -1 && threshold(u.aiGensThisMonth.used, u.aiGensThisMonth.effective)) return true;
  return false;
}

function cellHighlight(used: number, effective: number): string {
  if (effective === -1) return 'text-gray-700';
  if (effective === 0) return used > 0 ? 'text-red-600 font-semibold' : 'text-gray-700';
  const pct = used / effective;
  if (pct >= 1) return 'text-red-600 font-semibold';
  if (pct >= 0.8) return 'text-amber-600 font-medium';
  return 'text-gray-700';
}

export const AdminUsagePanel: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const [rows, setRows] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [onlyOverrides, setOnlyOverrides] = useState(false);
  const [onlyNearCap, setOnlyNearCap] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('storageUsedMb');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [overridesUser, setOverridesUser] = useState<UsageRow | null>(null);
  const [recomputingId, setRecomputingId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const authHeader = useMemo(
    (): HeadersInit => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const fetchSnapshot = useCallback(
    async (force = false) => {
      if (!token) return;
      if (force) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/admin/usage${force ? '?refresh=1' : ''}`,
          { headers: authHeader },
        );
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (!mountedRef.current) return;
        setRows(Array.isArray(json.users) ? json.users : []);
        setGeneratedAt(typeof json.generatedAt === 'string' ? json.generatedAt : null);
      } catch (err) {
        console.error('[admin/usage] load failed', err);
        toast.error(t('usage.errors.loadFailed'));
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [token, authHeader, t],
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchSnapshot(false);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchSnapshot]);

  // Auto-refresh every 5 min + whenever the tab becomes visible so
  // operators don't stare at stale data after returning from lunch.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchSnapshot(false);
    };
    const id = window.setInterval(() => fetchSnapshot(false), AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchSnapshot]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((u) => {
      if (q) {
        const hay = `${u.email} ${u.name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (planFilter !== 'all') {
        const normalised = u.subscription === 'plus' ? 'pro' : u.subscription;
        if (normalised !== planFilter) return false;
      }
      if (onlyOverrides && !u.hasOverrides) return false;
      if (onlyNearCap && !isNearCap(u)) return false;
      return true;
    });
  }, [rows, search, planFilter, onlyOverrides, onlyNearCap]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'email':
          return a.email.localeCompare(b.email) * dir;
        case 'subscription':
          return a.subscription.localeCompare(b.subscription) * dir;
        case 'pagesEffective':
          return (a.pages.effective - b.pages.effective) * dir;
        case 'storageUsedMb':
          return (a.storage.usedMb - b.storage.usedMb) * dir;
        case 'submissionsUsed':
          return (a.submissionsThisMonth.used - b.submissionsThisMonth.used) * dir;
        case 'aiUsed':
          return (a.aiGensThisMonth.used - b.aiGensThisMonth.used) * dir;
        default:
          return 0;
      }
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExportCsv = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/admin/usage/export.csv`, {
        headers: authHeader,
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const d = new Date();
      const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      a.download = `hitzlink-usage-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('usage.toasts.csvReady'));
    } catch (err) {
      console.error('[admin/usage] csv export failed', err);
      toast.error(t('usage.errors.csvFailed'));
    }
  };

  const handleRecompute = async (user: UsageRow) => {
    if (!token) return;
    setRecomputingId(user.id);
    try {
      const res = await fetch(`${API_URL}/admin/usage/recompute/${user.id}`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!res.ok) throw new Error(String(res.status));
      toast.success(t('usage.toasts.recomputed', { email: user.email }));
      await fetchSnapshot(true);
    } catch (err) {
      console.error('[admin/usage] recompute failed', err);
      toast.error(t('usage.errors.recomputeFailed'));
    } finally {
      setRecomputingId(null);
    }
  };

  const SortableTh: React.FC<{ sortKey_: SortKey; children: React.ReactNode }> = ({
    sortKey_,
    children,
  }) => {
    const active = sortKey === sortKey_;
    return (
      <th
        className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
        onClick={() => toggleSort(sortKey_)}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
        </span>
      </th>
    );
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Gauge className="w-8 h-8 text-amber-600" />
              {t('usage.title')}
            </h1>
            <p className="text-gray-600 mt-1">{t('usage.subtitle')}</p>
            {generatedAt && (
              <p className="text-xs text-gray-400 mt-1">
                {t('usage.generatedAt', {
                  time: new Date(generatedAt).toLocaleString(),
                })}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchSnapshot(true)}
              disabled={refreshing}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('usage.refresh')}
            </button>
            <button
              onClick={handleExportCsv}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {t('usage.exportCsv')}
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('usage.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value as PlanFilter)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('usage.filters.allPlans')}</option>
                <option value="free">{tierLabel('free')}</option>
                <option value="pro">{tierLabel('pro')}</option>
                <option value="agency">{tierLabel('agency')}</option>
                <option value="enterprise">{tierLabel('enterprise')}</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyOverrides}
                onChange={(e) => setOnlyOverrides(e.target.checked)}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              {t('usage.filters.onlyOverrides')}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyNearCap}
                onChange={(e) => setOnlyNearCap(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              {t('usage.filters.onlyNearCap')}
            </label>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh sortKey_="email">{t('usage.table.user')}</SortableTh>
                  <SortableTh sortKey_="subscription">{t('usage.table.plan')}</SortableTh>
                  <SortableTh sortKey_="pagesEffective">{t('usage.table.pages')}</SortableTh>
                  <SortableTh sortKey_="storageUsedMb">{t('usage.table.storage')}</SortableTh>
                  <SortableTh sortKey_="submissionsUsed">{t('usage.table.submissions')}</SortableTh>
                  <SortableTh sortKey_="aiUsed">{t('usage.table.aiGens')}</SortableTh>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('usage.table.overrides')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('usage.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                      {t('usage.loading')}
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      {t('usage.empty')}
                    </td>
                  </tr>
                ) : (
                  sorted.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate">{u.name}</div>
                        <div className="text-xs text-gray-500 truncate">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {tierLabel(u.subscription)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={cellHighlight(u.pages.used, u.pages.effective)}>
                          {u.pages.used}
                        </span>
                        <span className="text-gray-400"> / {formatCap(u.pages.effective)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={cellHighlight(u.storage.usedMb, u.storage.effective)}>
                          {u.storage.usedMb.toLocaleString()}
                        </span>
                        <span className="text-gray-400"> / {formatCap(u.storage.effective)} MB</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={cellHighlight(
                            u.submissionsThisMonth.used,
                            u.submissionsThisMonth.effective,
                          )}
                        >
                          {u.submissionsThisMonth.used.toLocaleString()}
                        </span>
                        <span className="text-gray-400">
                          {' '}
                          / {formatCap(u.submissionsThisMonth.effective)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={cellHighlight(
                            u.aiGensThisMonth.used,
                            u.aiGensThisMonth.effective,
                          )}
                        >
                          {u.aiGensThisMonth.used}
                        </span>
                        <span className="text-gray-400">
                          {' '}
                          / {formatCap(u.aiGensThisMonth.effective)}
                        </span>
                        {u.aiGensThisMonth.addonRemaining > 0 && (
                          <span className="ml-1 text-[10px] text-purple-600">
                            (+{u.aiGensThisMonth.addonRemaining} pack)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {u.hasOverrides ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                            title={u.overrideReason || ''}
                          >
                            <ShieldCheck className="w-3 h-3" />
                            {t('usage.overrideActive')}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowActions
                          user={u}
                          onOverrides={() => setOverridesUser(u)}
                          onRecompute={() => handleRecompute(u)}
                          onBroadcast={() => navigate(`/admin/broadcasts?userId=${u.id}`)}
                          recomputing={recomputingId === u.id}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {overridesUser && (
        <OverridesModal
          user={{
            id: overridesUser.id,
            email: overridesUser.email,
            name: overridesUser.name,
            subscription: overridesUser.subscription,
          }}
          onClose={() => setOverridesUser(null)}
          onSaved={() => fetchSnapshot(true)}
        />
      )}
    </AdminLayout>
  );
};

interface RowActionsProps {
  user: UsageRow;
  onOverrides: () => void;
  onRecompute: () => void;
  onBroadcast: () => void;
  recomputing: boolean;
}

const RowActions: React.FC<RowActionsProps> = ({
  user,
  onOverrides,
  onRecompute,
  onBroadcast,
  recomputing,
}) => {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative inline-block text-left">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        aria-label={`actions-${user.email}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 text-sm">
          <button
            onClick={() => {
              setOpen(false);
              onOverrides();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left"
          >
            <ShieldCheck className="w-4 h-4 text-amber-600" />
            {t('usage.actions.viewOverrides')}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              onRecompute();
            }}
            disabled={recomputing}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left disabled:opacity-50"
          >
            {recomputing ? (
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            ) : (
              <RefreshCcw className="w-4 h-4 text-blue-500" />
            )}
            {t('usage.actions.recompute')}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              onBroadcast();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left"
          >
            <Megaphone className="w-4 h-4 text-purple-500" />
            {t('usage.actions.sendBroadcast')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsagePanel;
